import type { StructureTree } from '@obraplan/core/domain/structure/structure-tree';

/**
 * As contas da grade pavimento × disciplina, fora do componente.
 *
 * A grade só **acrescenta**: a ligação que já existe aparece preenchida e não se desfaz
 * aqui — tirar uma disciplina de um pavimento pode levar junto cronograma e itens, e isso
 * se faz na árvore, onde a exclusão mostra o que vai junto.
 */

export const chaveDaLigacao = (floorId: string, disciplineId: string): string =>
  `${floorId}|${disciplineId}`;

export interface ColunaDoPavimento {
  readonly id: string;
  readonly torre: string;
  readonly nome: string;
}

/** Os pavimentos na ordem da árvore — torre por torre, do mais baixo ao mais alto. */
export function colunasDaEstrutura(tree: StructureTree): readonly ColunaDoPavimento[] {
  return tree.towers.flatMap((torre) =>
    torre.floors.map((andar) => ({
      id: andar.floor.id,
      torre: torre.tower.name,
      nome: andar.floor.name,
    })),
  );
}

/** O que a obra já ligou: cada atividade é um pavimento × disciplina. */
export function ligacoesExistentes(tree: StructureTree): ReadonlySet<string> {
  const chaves = new Set<string>();
  for (const torre of tree.towers) {
    for (const andar of torre.floors) {
      for (const { discipline } of andar.activities) {
        chaves.add(chaveDaLigacao(andar.floor.id, discipline.id));
      }
    }
  }
  return chaves;
}

/**
 * Liga ou desliga uma fileira inteira de células.
 *
 * Se todas as células livres da fileira já estão marcadas, o clique desmarca; senão,
 * marca as que faltam. É o gesto de uma planilha: clicar no título da linha a seleciona
 * toda, clicar de novo a solta. As já ligadas não entram na conta — não há o que fazer
 * com elas aqui.
 */
function alternarCelulas(
  selecao: ReadonlySet<string>,
  chaves: readonly string[],
  existentes: ReadonlySet<string>,
): Set<string> {
  const livres = chaves.filter((chave) => !existentes.has(chave));
  const proxima = new Set(selecao);
  const todasMarcadas = livres.length > 0 && livres.every((chave) => selecao.has(chave));
  for (const chave of livres) {
    if (todasMarcadas) proxima.delete(chave);
    else proxima.add(chave);
  }
  return proxima;
}

export function alternarDisciplina(
  selecao: ReadonlySet<string>,
  disciplineId: string,
  floorIds: readonly string[],
  existentes: ReadonlySet<string>,
): Set<string> {
  return alternarCelulas(
    selecao,
    floorIds.map((floorId) => chaveDaLigacao(floorId, disciplineId)),
    existentes,
  );
}

export function alternarPavimento(
  selecao: ReadonlySet<string>,
  floorId: string,
  disciplineIds: readonly string[],
  existentes: ReadonlySet<string>,
): Set<string> {
  return alternarCelulas(
    selecao,
    disciplineIds.map((disciplineId) => chaveDaLigacao(floorId, disciplineId)),
    existentes,
  );
}

export interface LoteDeLigacao {
  readonly floorIds: readonly string[];
  readonly disciplineIds: readonly string[];
}

/**
 * A seleção da grade em chamadas de "aplicar".
 *
 * O caso de uso cruza uma lista de pavimentos com uma de disciplinas; a grade marca células
 * soltas. As disciplinas que foram marcadas **nos mesmos pavimentos** viram um lote só —
 * o caso comum ("estas cinco, do 1º ao 12º") é uma chamada, e não cinco.
 */
export function lotesParaAplicar(selecao: ReadonlySet<string>): readonly LoteDeLigacao[] {
  const andaresPorDisciplina = new Map<string, string[]>();
  for (const chave of selecao) {
    const [floorId, disciplineId] = chave.split('|') as [string, string];
    const lista = andaresPorDisciplina.get(disciplineId) ?? [];
    lista.push(floorId);
    andaresPorDisciplina.set(disciplineId, lista);
  }

  const lotes = new Map<string, { floorIds: string[]; disciplineIds: string[] }>();
  for (const [disciplineId, floorIds] of andaresPorDisciplina) {
    const ordenados = [...floorIds].sort();
    const assinatura = ordenados.join(',');
    const lote = lotes.get(assinatura) ?? { floorIds: ordenados, disciplineIds: [] };
    lote.disciplineIds.push(disciplineId);
    lotes.set(assinatura, lote);
  }
  return [...lotes.values()];
}
